export enum FlowSentimentLabel {
  Bullish = 'Bullish',
  SlightlyBullish = 'Slightly Bullish',
  Mixed = 'Mixed',
  SlightlyBearish = 'Slightly Bearish',
  Bearish = 'Bearish',
  Neutral = 'Neutral',
}

interface DirectionalFlowBreakdownInput {
  bullish: number;
  bearish: number;
  neutral: number;
}

export interface FlowSentimentBreakdown extends DirectionalFlowBreakdownInput {
  total: number;
  directionalTotal: number;
  score: number;
  bullishShare: number;
  bearishShare: number;
  neutralShare: number;
  label: FlowSentimentLabel;
}

const STRONG_SENTIMENT_THRESHOLD = 0.5;
const SLIGHT_SENTIMENT_THRESHOLD = 0.1;

const getFlowSentimentLabel = (score: number, directionalTotal: number): FlowSentimentLabel => {
  if (directionalTotal === 0) {
    return FlowSentimentLabel.Neutral;
  }

  if (score >= STRONG_SENTIMENT_THRESHOLD) {
    return FlowSentimentLabel.Bullish;
  }

  if (score > SLIGHT_SENTIMENT_THRESHOLD) {
    return FlowSentimentLabel.SlightlyBullish;
  }

  if (score >= -SLIGHT_SENTIMENT_THRESHOLD) {
    return FlowSentimentLabel.Mixed;
  }

  if (score >= -STRONG_SENTIMENT_THRESHOLD) {
    return FlowSentimentLabel.SlightlyBearish;
  }

  return FlowSentimentLabel.Bearish;
};

export const calculateFlowSentimentBreakdown = ({
  bullish,
  bearish,
  neutral,
}: DirectionalFlowBreakdownInput): FlowSentimentBreakdown => {
  const safeBullish = Math.max(bullish || 0, 0);
  const safeBearish = Math.max(bearish || 0, 0);
  const safeNeutral = Math.max(neutral || 0, 0);
  const directionalTotal = safeBullish + safeBearish;
  const total = directionalTotal + safeNeutral;
  const score = total === 0 ? 0 : (safeBullish - safeBearish) / total;

  return {
    bullish: safeBullish,
    bearish: safeBearish,
    neutral: safeNeutral,
    total,
    directionalTotal,
    score,
    bullishShare: total === 0 ? 0 : safeBullish / total,
    bearishShare: total === 0 ? 0 : safeBearish / total,
    neutralShare: total === 0 ? 0 : safeNeutral / total,
    label: getFlowSentimentLabel(score, directionalTotal),
  };
};
